import React from 'react';
import { Download, Loader2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle
} from '../ui/dialog';
import { useAuth } from '../../hooks';
import { useMergedPdf } from './hooks/useMergedPdf';
import ActivityPdfViewer from './ActivityPdfViewer';

const toFileName = (title) => {
  const safe = String(title || 'submission')
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '');
  return `${safe || 'submission'}.pdf`;
};

function ActivityPdfModal({ activity, open, onClose }) {
  const { token } = useAuth();
  const title = activity?.title || 'Submission';

  const { blobUrl, isLoading, errorMessage } = useMergedPdf({
    studentAssignmentId: activity?.studentAssignmentId || null,
    answerSheetUrl: activity?.pdfUrl || null,
    pdfKey: activity?.pdfKey || null,
    enabled: Boolean(open && activity),
    token
  });

  const handleDownload = () => {
    if (!blobUrl) return;
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = toFileName(title);
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="inset-0 flex h-[100dvh] max-h-[100dvh] w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 bg-[#111827] p-0 shadow-none"
      >
        <div className="flex flex-shrink-0 items-center gap-3 border-b border-gray-700 px-3 py-3 sm:px-4">
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/10"
            aria-label="Close PDF"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>

          <div className="min-w-0 flex-1">
            <DialogTitle className="truncate text-base font-semibold text-white">
              {title}
            </DialogTitle>
            <DialogDescription className="truncate text-xs text-gray-400">
              {activity?.details || 'Evaluated submission'}
            </DialogDescription>
          </div>

          <button
            type="button"
            onClick={handleDownload}
            disabled={!blobUrl}
            className="inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-lg bg-white px-3 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-4 w-4" strokeWidth={2} />
            Download
          </button>
        </div>

        <div className="relative min-h-0 flex-1 bg-gray-900">
          {isLoading ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-gray-300">
              <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
              <p className="text-sm">Preparing document...</p>
            </div>
          ) : blobUrl ? (
            <div className="absolute inset-0">
              <ActivityPdfViewer fileUrl={blobUrl} />
            </div>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="text-sm text-gray-300">{errorMessage || 'PDF is not available.'}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ActivityPdfModal;
